export interface AnswerDTO {
  type: 'answers'; // Type of the object
  id: string; // ID of the answer
  attributes: {
    user_id: string; // ID of the user who answered
    user_name: string; // Name of the user who answered, can be null
    user_picture: string; // URL of the user's picture
    question_id: string; // ID of the related question
    answer: string; // Text of the answer
    status: string; // Status of the answer
    status_reason: string; // Reason for the status, can be empty
    has_bought: number; // Indicates if it has been purchased or not
    votes: number; // Total number of votes
    positive_votes: number; // Number of positive votes
    negative_votes: number; // Number of negative votes
    created_at: string; // Creation date of the answer
    updated_at: string | null; // Update date, can be null
    publication_date: string | null; // Publication date, can be null
    best_answer: number; // Indicates if it is the best answer (1 for yes, 0 for no)
    attachments: AttachmentAttributes[];
  };
}

// Enum for question status
export enum QuestionStatus {
  Check = 'check',
  Rejected = 'rejected',
  Active = 'active',
}

// Enum for unanswered filter
export enum UnansweredFilter {
  True = 'true',
  False = 'false',
}

// Enum for included relationships
export enum IncludeRelationships {
  Answers = 'answers.attachments',
}

// Enum for sort order
export enum SortOrder {
  CreatedAt = 'created_at',
  PositiveVotes = 'positive_votes',
  NegativeVotes = 'negative_votes',
  Votes = 'votes',
  Status = 'status',
  Question = 'question',
  CreatedAtDesc = '-created_at',
  PositiveVotesDesc = '-positive_votes',
  NegativeVotesDesc = '-negative_votes',
  VotesDesc = '-votes',
  StatusDesc = '-status',
  QuestionDesc = '-question',
}

// Interface for query filters (GET /questions)
export interface GetQuestionsQueryParamsDTO {
  'filter[article_id]'?: string; // Article ID
  'filter[user_id]'?: string; // User ID
  'filter[category_ids][]'?: string[]; // Category IDs
  'filter[status]'?: QuestionStatus; // Question status
  sort?: SortOrder; // Sort order
  search?: string; // Search term
  include?: IncludeRelationships; // Included relationships
  'filter[unanswered]'?: UnansweredFilter; // Unanswered filter
  'page[size]'?: number;
  'page[number]'?: number;
}

export interface GetQuestionQueryParamsDTO {
  include?: IncludeRelationships; // Included relationships
}

// Interface for creating a new question (POST /questions)
export interface CreateQuestionRequestDTO {
  user_id: string; // The user ID of the creator
  article_id: string; // Article ID of the question
  category_id: string; // Category ID of the article
  question: string; // The question text
  user_name: string; // The name of the user
  article_name: string; // The name of the article
  brand_id: string;
  send_notification: boolean;
  user_picture: string;
  sku: string;
}

// Success response for the GET /questions endpoint
export interface QuestionDTO {
  type: 'questions'; // Type of object
  id: string; // ID of the question
  attributes: {
    answers_count: number; // Count of answers
    user_id: string; // ID of the user who asked the question
    user_name: string; // Name of the user who asked the question
    user_picture: string; // URL of the user's picture
    article_id: string; // ID of the related article
    best_answer: string | null; // ID of the best answer, can be null
    category_id: string; // ID of the category
    article_name: string; // Name of the article
    question: string; // Text of the question
    status: string; // Status of the question
    status_reason: string; // Reason for the status, can be empty
    has_bought: number; // Indicates if it has been purchased or not
    send_notification: number; // Indicates if a notification should be sent
    votes: number | null; // Total number of votes, can be null
    positive_votes: number | null; // Number of positive votes, can be null
    negative_votes: number | null; // Number of negative votes, can be null
    created_at: string; // Creation date of the question
    updated_at: string | null; // Update date, can be null
    publication_date: string; // Publication date
  };
  relationships: {
    answers: AnswerDTO[]; // List of associated answers
  };
}

// Response for a collection of questions (GET /questions)
export interface IndexQuestionCollectionResourceDTO {
  data: QuestionDTO[];
  links: PaginationLinks; // Pagination links
  meta: PaginationMeta; // Meta information about the pagination
}

// Success response for POST /questions
export interface StoreQuestionResourceDTO {
  data: QuestionDTO;
}

// Bad request response (422 Unprocessable Entity)
export interface GeneralBadRequestDTO {
  error: string;
  message: string;
  fields: { [key: string]: string }; // Field-specific error messages
}

// Server error response (500)
export interface ServerErrorRequestDTO {
  error: string;
  message: string;
}

export interface AnswerVoteResourceDTO {
  vote: '1' | '0'; // "1" for positive, "0" for negative
  user_id: string; // The user ID of the person voting
}


export interface PostAnswerRequestDTO {
  user_id: string; // The user ID of the creator
  user_name: string; // The name of the user
  question_id: string; // The ID of the question to which this answer belongs
  answer: string; // The answer text
  videos?: string[]; // Array of YouTube URLs attached to the answer (optional)
  urls?: string[]; // Array of generic URLs attached to the answer (optional)
  images?: string[]; // Array of Image base64 attached to the answer (optional)
}

// DTO for fetching an answer resource
export interface GetAnswerResourceDTO {
  data: AnswerData; // Contains the fetched answer data
}

// DTO for the answer data structure
export interface AnswerData {
  type: 'answers'; // Type of the resource
  id: string; // The ID of the answer
  attributes: AnswerAttributes; // The attributes of the answer
  relationships: AnswerRelationships; // Relationships related to the answer
}

// DTO for answer attributes
export interface AnswerAttributes {
  user_id: string; // The user ID of the answer creator
  question_id: string; // The ID of the associated question
  answer: string; // The text of the answer
  status: string; // The status of the answer (e.g., "active")
  status_reason?: string | null; // Reason for the status (optional)
  has_bought: number; // Indicates if the answer has been bought (0 or 1)
  votes: number; // Total votes received
  positive_votes: number; // Total positive votes
  negative_votes: number; // Total negative votes
  created_at: string; // Creation date in ISO 8601 format
  updated_at: string; // Update date in ISO 8601 format
  publication_date?: string | null; // Publication date (optional)
}


// DTO for the question relationship
export interface QuestionRelationship {
  type: 'questions'; // Type of the resource
  id: string; // The ID of the question
  attributes: QuestionAttributes; // The attributes of the question
}

// DTO for question attributes
export interface QuestionAttributes {
  user_id: string; // The user ID of the question creator
  article_id: string; // Article ID associated with the question
  question: string; // The text of the question
  status: string; // The status of the question
  status_reason?: string | null; // Reason for the status (optional)
  has_bought: number; // Indicates if the question has been bought (0 or 1)
  votes: number; // Total votes received for the question
  positive_votes: number; // Total positive votes for the question
  negative_votes: number; // Total negative votes for the question
  created_at: string; // Creation date in ISO 8601 format
  updated_at: string; // Update date in ISO 8601 format
  publication_date?: string | null; // Publication date (optional)
}


// DTO for indexing answers collection resource
export interface IndexAnswerCollectionResourceDTO {
  data: AnswerData[]; // Array of fetched answers
  links: PaginationLinks; // Pagination links
  meta: PaginationMeta; // Meta information about the pagination
}

// DTO for individual answer data
export interface AnswerData {
  type: 'answers'; // Type of the resource
  id: string; // The ID of the answer
  attributes: AnswerAttributes; // The attributes of the answer
  relationships: AnswerRelationships; // Relationships related to the answer
}

// DTO for answer attributes

// DTO for answer relationships
export interface AnswerRelationships {
  attachments: AttachmentData[]; // List of attachments related to the answer
  question: QuestionRelationship; // The related question
}

// DTO for attachment data
export interface AttachmentData {
  type: 'attachments'; // Type of the attachment
  id: string; // The ID of the attachment
  attributes: AttachmentAttributes; // The attributes of the attachment
}

// DTO for attachment attributes
export interface AttachmentAttributes {
  answer_id: string; // The ID of the associated answer
  url: string; // URL of the attachment
  type: 'youtube' | 'url' | 'image'; // Type of the attachment
  created_at: string; // Creation date in ISO 8601 format
  updated_at: string; // Update date in ISO 8601 format
}

// DTO for question relationship
export interface QuestionRelationship {
  type: 'questions'; // Type of the resource
  id: string; // The ID of the question
  attributes: QuestionAttributes; // The attributes of the question
}

// DTO for pagination links
export interface PaginationLinks {
  first: string; // URL to the first page
  last: string; // URL to the last page
  prev: string | null; // URL to the previous page (nullable)
  next: string | null; // URL to the next page (nullable)
}

// DTO for pagination meta
export interface PaginationMeta {
  current_page: number; // Current page number
  from: number; // Starting index of the items on the current page
  last_page: number; // Total number of pages
  links: PaginationLink[]; // Array of pagination link objects
  path: string; // Base URL for pagination
  per_page: number; // Number of items per page
  to: number; // Ending index of the items on the current page
  total: number; // Total number of items
}

// DTO for a single pagination link
export interface PaginationLink {
  url: string | null; // URL for the pagination link (nullable)
  label: string; // Display label for the pagination link
  active: boolean; // Indicates if the link is active
}

export interface GetAnswersQueryParamsDTO {
  'filter[user_id]'?: string; // User ID
  'filter[question_id]'?: string; // Question ID
  'filter[article_id]'?: string; // Article ID
  'filter[status]'?: QuestionStatus; // Question status
  'page[size]'?: number; // Number of items per page
  sort?: string;
  search?: string;
  include?: 'question';
}

export interface NewAnswerAttributes {
  user_id: string; // The user ID of the answer creator
  question_id: string; // The ID of the associated question
  answer: string; // The text of the answer
  status: string | null; // The status of the answer (nullable)
  status_reason?: string | null; // Reason for the status (optional, nullable)
  has_bought: number | null; // Indicates if the answer has been bought (0 or 1, nullable)
  votes: number | null; // Total votes received (nullable)
  positive_votes: number | null; // Total positive votes (nullable)
  negative_votes: number | null; // Total negative votes (nullable)
  created_at: string; // Creation date in ISO 8601 format
  updated_at: string; // Update date in ISO 8601 format
  publication_date?: string | null; // Publication date (optional)
}

export interface NewAnswerData {
  type: 'answers'; // Type of the resource
  id: string; // The ID of the newly created answer
  attributes: NewAnswerAttributes; // The attributes of the newly created answer
}

export interface StoreAnswerResourceDTO {
  data: NewAnswerData; // The newly created answer data
}

export interface GetAnswerQueryParamsDTO {
  include?: 'attachments' | 'question' | 'attachments,question';
}

export interface UpdateAnswerRequestDTO {
  user_id?: string;
  user_name?: string;
  question_id?: string;
  answer?: string;
  videos?: string[];
  urls?: string[];
}
